import React from "react";
import {
  definePlugin,
  PanelSection,
  PanelSectionRow,
  TextField,
  ButtonItem,
  ToggleField,
  Focusable,
} from "@decky/ui";
import { initStorePatch } from "./StorePatch";
import { initLibraryPatch, initContextMenuPatch, refreshCr0wbarFixes } from "./LibraryPatch";

interface ServerAPI {
  callPluginMethod(method: string, args?: any): Promise<any>;
  toaster?: any;
  routerHook?: {
    addPatch(route: string, fn: (tree: any) => any): any;
    removePatch(route: string, patch: any): void;
  };
}

type BackupEntry = { name: string; stamp: string; size: number; mtime: number };
type SearchResult = { app_id: string; title: string; delisted?: boolean };

// ── Search Tab ───────────────────────────────────────────────────────────────
const SearchTab: React.FC<{ serverAPI: ServerAPI }> = ({ serverAPI }) => {
  const [query, setQuery] = React.useState("");
  const [searchByAppId, setSearchByAppId] = React.useState(false);
  const [results, setResults] = React.useState<SearchResult[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [injecting, setInjecting] = React.useState<string | null>(null);
  const [msg, setMsg] = React.useState("");

  const notify = (title: string, body: string) => {
    serverAPI?.toaster?.toast?.({ title, body, duration: 5000 });
  };

  const doSearch = async () => {
    const q = query.trim();
    const minLen = searchByAppId ? 1 : 3;
    if (q.length < minLen) {
      setMsg(searchByAppId ? "Enter an App ID" : "Enter at least 3 characters");
      return;
    }
    setSearching(true);
    setMsg("");
    try {
      const r = await serverAPI.callPluginMethod("search_games", {
        query: q,
        limit: 20,
        appid: searchByAppId,
      });
      if (r?.result?.error) {
        setMsg(r.result.error);
        setResults([]);
      } else {
        const res = r?.result?.results ?? [];
        setResults(res);
        setMsg(res.length === 0 ? "No results found" : `${res.length} result${res.length !== 1 ? "s" : ""}`);
      }
    } catch (e: any) {
      setMsg(`Search error: ${e}`);
      setResults([]);
    }
    setSearching(false);
  };

  const doInject = async (appId: string, title: string) => {
    setInjecting(appId);
    setMsg(`Adding ${title} to SLS...`);
    try {
      const r = await serverAPI.callPluginMethod("inject_game", { app_id: appId });
      if (r?.success && !r.result?.error) {
        const name = r.result?.name ?? title;
        const delisted = r.result?.delisted;
        const depots = r.result?.depots?.length ?? 0;
        const msg = delisted
          ? `Added ${name} to SLS (delisted, ${depots} depots)`
          : `Added ${name} to SLS`;
        notify("Hollow-decky", msg);
        setMsg(msg);
      } else {
        const err = r?.result?.error || "Unknown error";
        notify("Hollow-decky", err);
        setMsg(err);
      }
    } catch (e: any) {
      notify("Hollow-decky", `Error: ${e}`);
      setMsg(`Error: ${e}`);
    }
    setInjecting(null);
  };

  return (
    <>
      <PanelSection title="Search Games">
        <PanelSectionRow>
          <TextField
            label={searchByAppId ? "App ID (numeric)" : "Game Name"}
            value={query}
            onChange={(e: any) => setQuery(e.target.value)}
          />
        </PanelSectionRow>
        <PanelSectionRow>
          <ToggleField
            label="Search by App ID"
            description="Match exact game_id instead of name"
            checked={searchByAppId}
            onChange={setSearchByAppId}
          />
        </PanelSectionRow>
        <PanelSectionRow>
          <ButtonItem disabled={searching || query.trim().length < (searchByAppId ? 1 : 3)} onClick={doSearch}>
            {searching ? "Searching..." : "Search"}
          </ButtonItem>
        </PanelSectionRow>
      </PanelSection>

      {msg && (
        <PanelSectionRow>
          <div style={{ fontSize: "12px", opacity: 0.8, padding: "8px" }}>{msg}</div>
        </PanelSectionRow>
      )}

      {results.length > 0 && (
        <PanelSection title={`Results (${results.length})`}>
          {results.map((r: SearchResult) => (
            <PanelSectionRow key={r.app_id}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
                padding: "6px 0",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "14px", color: "#e0e0e0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.title}
                    {r.delisted && (
                      <span style={{ fontSize: "10px", color: "#f44336", marginLeft: "6px", fontWeight: 600 }}>DELISTED</span>
                    )}
                  </div>
                  <div style={{ fontSize: "11px", color: "#888", fontFamily: "monospace" }}>
                    {r.app_id}
                  </div>
                </div>
                <ButtonItem
                  disabled={injecting === r.app_id}
                  onClick={() => doInject(r.app_id, r.title)}
                >
                  {injecting === r.app_id ? "..." : "Inject"}
                </ButtonItem>
              </div>
            </PanelSectionRow>
          ))}
        </PanelSection>
      )}
    </>
  );
};

// ── Settings Tab ─────────────────────────────────────────────────────────────
const SettingsTab: React.FC<{ serverAPI: ServerAPI }> = ({ serverAPI }) => {
  const [showRestartButton, setShowRestartButton] = React.useState(true);
  const [addAppIdToSls, setAddAppIdToSls] = React.useState(false);
  const [buttonLayout, setButtonLayout] = React.useState<"inline" | "original">("inline");
  const [msg, setMsg] = React.useState("");
  const [cr0wbarCount, setCr0wbarCount] = React.useState<number | null>(null);
  const [cr0wbarLoading, setCr0wbarLoading] = React.useState(false);

  const loadCr0wbarCount = async () => {
    setCr0wbarLoading(true);
    try {
      const r = await serverAPI.callPluginMethod("fetch_cr0wbar_fix_list", {});
      if (r?.result?.count !== undefined) {
        setCr0wbarCount(r.result.count);
      }
    } catch {}
    setCr0wbarLoading(false);
  };

  const refreshCr0wbar = async () => {
    setCr0wbarLoading(true);
    try {
      const r = await serverAPI.callPluginMethod("fetch_cr0wbar_fix_list", { force: true });
      if (r?.result?.count !== undefined) {
        setCr0wbarCount(r.result.count);
        setMsg(`Cr0wbar: ${r.result.count} fixes loaded`);
      } else if (r?.result?.error) {
        setMsg(`Cr0wbar error: ${r.result.error}`);
      }
    } catch (e: any) {
      setMsg(`Cr0wbar error: ${e}`);
    }
    setCr0wbarLoading(false);
  };

  React.useEffect(() => {
    (async () => {
      const [sr, slsR, blR] = await Promise.all([
        serverAPI.callPluginMethod("get_setting", { key: "show_restart_button", default: true }),
        serverAPI.callPluginMethod("get_setting", { key: "add_app_id_to_sls", default: false }),
        serverAPI.callPluginMethod("get_setting", { key: "button_layout", default: "inline" }),
      ]);
      if (sr?.success) setShowRestartButton(sr.result ?? true);
      if (slsR?.success) setAddAppIdToSls(slsR.result ?? false);
      if (blR?.success) setButtonLayout(blR.result === "original" ? "original" : "inline");
      loadCr0wbarCount();
    })();
  }, []);

  const toggleRestartButton = async (val: any) => {
    const checked = typeof val === "boolean" ? val : !!(val?.target?.checked ?? val?.currentTarget?.checked);
    setShowRestartButton(checked);
    await serverAPI.callPluginMethod("set_setting", { key: "show_restart_button", value: checked });
  };

  const toggleAddAppIdToSls = async (val: any) => {
    const checked = typeof val === "boolean" ? val : !!(val?.target?.checked ?? val?.currentTarget?.checked);
    setAddAppIdToSls(checked);
    await serverAPI.callPluginMethod("set_setting", { key: "add_app_id_to_sls", value: checked });
  };

  const toggleButtonLayout = async (val: any) => {
    const checked = typeof val === "boolean" ? val : !!(val?.target?.checked ?? val?.currentTarget?.checked);
    const layout = checked ? "original" : "inline";
    setButtonLayout(layout);
    await serverAPI.callPluginMethod("set_setting", { key: "button_layout", value: layout });
  };

  return (
    <>
      {/* Behavior */}
      <PanelSection title="Behavior">
        <PanelSectionRow>
          <ToggleField
            label="Show Restart Steam button"
            description="Show the manual Restart Steam button on store pages."
            checked={showRestartButton}
            onChange={toggleRestartButton}
          />
        </PanelSectionRow>
        <PanelSectionRow>
          <ToggleField
            label="Add AppId to SLS"
            description="Also add the game's AppId to SLS AdditionalApps section when injecting."
            checked={addAppIdToSls}
            onChange={toggleAddAppIdToSls}
          />
        </PanelSectionRow>
        <PanelSectionRow>
          <ToggleField
            label="Original Button Layout"
            description="Use fixed corner buttons instead of inline below tags. Toggle off for gamepad-focusable inline buttons."
            checked={buttonLayout === "original"}
            onChange={toggleButtonLayout}
          />
        </PanelSectionRow>
      </PanelSection>

      {/* Cr0wbar Fixes */}
      <PanelSection title="Cr0wbar Fixes">
        <PanelSectionRow>
          <div style={{ background: "#1a1a24", borderRadius: "10px", padding: "14px", border: "1px solid #2a2a3a" }}>
            <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>
              Game fixes from cr0wbar-fixes (GitHub)
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <span style={{ color: "#aaa", fontSize: "13px" }}>
                {cr0wbarCount !== null ? `${cr0wbarCount} fixes available` : "Not loaded"}
              </span>
              <ButtonItem onClick={refreshCr0wbar} disabled={cr0wbarLoading}>
                {cr0wbarLoading ? "Loading..." : "Refresh"}
              </ButtonItem>
            </div>
            <div style={{ fontSize: "11px", color: "#666" }}>
              Fixes appear as "INSTALL FIX" buttons on Library game pages
            </div>
          </div>
        </PanelSectionRow>
      </PanelSection>

      {msg && <PanelSectionRow><div style={{ fontSize: "12px", opacity: 0.8, padding: "8px" }}>{msg}</div></PanelSectionRow>}
    </>
  );
};

// ── Backups Tab ──────────────────────────────────────────────────────────────
const BackupsTab: React.FC<{ serverAPI: ServerAPI }> = ({ serverAPI }) => {
  const [backups, setBackups] = React.useState<BackupEntry[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  const notify = (title: string, body: string) => {
    serverAPI?.toaster?.toast?.({ title, body, duration: 5000 });
  };

  const load = async () => {
    setBusy(true);
    const r = await serverAPI.callPluginMethod("list_backups", {});
    if (r?.success) setBackups(r.result ?? []);
    setBusy(false);
  };

  const create = async () => {
    setBusy(true);
    const r = await serverAPI.callPluginMethod("create_backup", {});
    if (r?.success) { setMsg(`Created: ${r.result}`); load(); }
    else setMsg("Failed");
    setBusy(false);
  };

  const restore = async (name: string) => {
    setBusy(true);
    const r = await serverAPI.callPluginMethod("restore_backup", { backup_name: name });
    if (r?.result?.error) {
      setMsg(`Error: ${r.result.error}`);
      notify("Hollow-decky", `Restore failed: ${r.result.error}`);
    } else {
      const text = r?.result?.verified ? `Restored ${name}` : `Restore not verified: ${name}`;
      setMsg(text);
      notify("Hollow-decky", text);
      await load();
    }
    setBusy(false);
  };

  React.useEffect(() => { load(); }, []);

  return (
    <>
      <PanelSectionRow><ButtonItem disabled={busy} onClick={create}>Create Backup Now</ButtonItem></PanelSectionRow>
      <PanelSection title={`Backups (${backups.length})`}>
        {backups.length === 0 && <PanelSectionRow><div style={{ opacity: 0.6, padding: "8px" }}>None</div></PanelSectionRow>}
        {backups.map((b: BackupEntry) => {
          // Format stamp: "sls.20260529T202720Z" → "2026-05-29 20:27"
          // or "pre_restore.20260529T202720Z" → "Pre-restore · 2026-05-29 20:27"
          const isPreRestore = b.stamp.startsWith("pre_restore.");
          const rawTs = isPreRestore ? b.stamp.slice("pre_restore.".length) : b.stamp.replace("sls.", "");
          // Parse YYYYMMDDTHHMMSSZ
          const dateMatch = rawTs.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
          const displayTs = dateMatch
            ? `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
            : rawTs;
          const label = isPreRestore ? `Pre-restore · ${displayTs}` : displayTs;

          return (
            <PanelSectionRow key={b.name}>
              <ButtonItem onClick={() => restore(b.name)} layout="below">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  <span style={{ fontSize: "13px" }}>{label}</span>
                  <span style={{ fontSize: "11px", color: "#888", marginLeft: "8px" }}>{Math.round(b.size / 1024)} KB</span>
                </div>
              </ButtonItem>
            </PanelSectionRow>
          );
        })}
      </PanelSection>
      {msg && <PanelSectionRow><div style={{ fontSize: "12px", opacity: 0.8, padding: "8px" }}>{msg}</div></PanelSectionRow>}
    </>
  );
};

// ── Plugin Entry ─────────────────────────────────────────────────────────────
export default definePlugin((serverAPI: ServerAPI) => {
  const storePatchCleanup = initStorePatch(serverAPI);
  const libraryPatchCleanup = initLibraryPatch(serverAPI);
  const contextMenuCleanup = initContextMenuPatch(serverAPI);

  // Pre-load cr0wbar fixes list
  refreshCr0wbarFixes();

  const Content = () => {
    const [activeTab, setActiveTab] = React.useState("search");

    const tabStyle = (active: boolean): any => ({
      flex: 1,
      textAlign: "center" as const,
      padding: "8px 4px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: active ? 700 : 400,
      color: active ? "#fff" : "#999",
      background: active ? "rgba(255,255,255,0.08)" : "transparent",
      borderRadius: "6px",
      border: active ? "1px solid rgba(255,255,255,0.15)" : "1px solid transparent",
      transition: "background 0.15s, border-color 0.15s, color 0.15s",
      userSelect: "none" as const,
      WebkitUserSelect: "none" as const,
      outline: "none" as const,
    });

    const focusClass = "hollow-tab-focus";
    const activeFocusClass = "hollow-tab-active-focus";

    return (
      <>
        <style>{`
          .${focusClass}{background:rgba(255,255,255,0.12) !important;color:#ddd !important;border-color:rgba(255,255,255,0.25) !important}
          .${activeFocusClass}{background:rgba(255,255,255,0.18) !important;color:#fff !important;border-color:rgba(255,255,255,0.35) !important}
        `}</style>
        {/* Tab bar — Focusable for gamepad support */}
        <Focusable flow-children="horizontal" style={{ display: "flex", gap: "4px", padding: "0 0 12px 0" }}>
          <Focusable focusClassName={activeTab === "search" ? activeFocusClass : focusClass} onActivate={() => setActiveTab("search")} style={tabStyle(activeTab === "search")}>Search</Focusable>
          <Focusable focusClassName={activeTab === "settings" ? activeFocusClass : focusClass} onActivate={() => setActiveTab("settings")} style={tabStyle(activeTab === "settings")}>Settings</Focusable>
          <Focusable focusClassName={activeTab === "backups" ? activeFocusClass : focusClass} onActivate={() => setActiveTab("backups")} style={tabStyle(activeTab === "backups")}>Backups</Focusable>
        </Focusable>
        {/* Tab content */}
        {activeTab === "search" && <SearchTab serverAPI={serverAPI} />}
        {activeTab === "settings" && <SettingsTab serverAPI={serverAPI} />}
        {activeTab === "backups" && <BackupsTab serverAPI={serverAPI} />}
      </>
    );
  };

  return {
    title: "Hollow-decky",
    content: <Content />,
    icon: <div style={{ fontSize: "18px", fontWeight: "bold" }}>⛓</div>,
    onDismount() {
      storePatchCleanup();
      libraryPatchCleanup();
      contextMenuCleanup();
    },
  };
});